"use client";

import Link from "next/link";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, FileImage, LoaderCircle, Star } from "lucide-react";
import { Attachment, AttachmentContent, AttachmentDescription, AttachmentMedia, AttachmentTitle } from "@/components/ui/attachment";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Bubble, BubbleContent } from "@/components/ui/bubble";
import { ReviewConversation } from "@/components/review-conversation";
import { toast } from "sonner";

type Review = {
  id: string;
  author_name: string;
  rating: number;
  title: string | null;
  content: string;
  service_name: string;
  created_at: string;
  comments?: Array<{ id: string; author_name: string; author_role?: "owner" | "client"; content: string; created_at: string; profile_photo_url?: string | null }>;
  photoUrl: string | null;
  profilePhotoUrl: string | null;
  canReply?: boolean;
};

type Appointment = {
  id: string;
  service_name: string;
  start_at: string;
  status: string;
  review_id: string | null;
};

function Stars({ rating }: { rating: number }) {
  return <span className="review-stars" aria-label={`${rating} étoiles sur 5`}>{Array.from({ length: 5 }, (_, index) => <Star key={index} size={16} fill={index < rating ? "currentColor" : "none"} />)}</span>;
}

function formatReviewDate(value: string) {
  const date = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

export function ReviewsPanel() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [average, setAverage] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [reviewsResponse, activityResponse] = await Promise.all([
        fetch("/api/reviews", { cache: "no-store" }),
        fetch("/api/my/activity", { cache: "no-store" }),
      ]);
      const [reviewsData, activityData] = await Promise.all([reviewsResponse.json(), activityResponse.json()]);
      if (!reviewsResponse.ok || !activityResponse.ok) {
        throw new Error(reviewsData.error || activityData.error || "Impossible de charger les avis.");
      }
      setReviews(reviewsData.reviews || []);
      setAverage(reviewsData.average || 0);
      setReviewCount(reviewsData.count || 0);
      setAppointments(activityData.appointments || []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Impossible de charger les avis.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => { void load(); }, 0);
    return () => window.clearTimeout(timeout);
  }, [load]);

  const eligibleAppointments = useMemo(() => appointments.filter((appointment) => appointment.status === "completed" && !appointment.review_id), [appointments]);

  async function replyToReview(event: FormEvent<HTMLFormElement>, reviewId: string) {
    event.preventDefault();
    setReplying(true);
    setError("");
    try {
      const response = await fetch(`/api/reviews/${reviewId}/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content: replyText }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "La réponse n’a pas pu être publiée.");
      setReplyText("");
      setReplyingId(null);
      toast.success("Réponse publiée.");
      await load();
    } catch (replyError) { setError(replyError instanceof Error ? replyError.message : "La réponse n’a pas pu être publiée."); } finally { setReplying(false); }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setSubmitting(true);
    setSuccess(false);
    setError("");
    const form = new FormData(formElement);

    try {
      const response = await fetch("/api/reviews", {
        method: "POST",
        body: form,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "L’avis n’a pas pu être envoyé.");
      setSuccess(true);
      toast.success("Avis envoyé", { description: "Il sera visible après validation." });
      formElement.reset();
      await load();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "L’avis n’a pas pu être envoyé.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="section reviews-page-section">
      <div className="container reviews-layout">
        <div className="reviews-list-column">
          <div className="reviews-summary"><strong>{average || "—"}</strong><div><Stars rating={Math.round(average)} /><span>{reviewCount} avis publié(s)</span></div></div>
          {loading && <div className="panel-loading"><LoaderCircle className="spin" /> Chargement des avis…</div>}
          {!loading && reviews.length === 0 && <div className="empty-panel"><h2>Aucun avis publié</h2><p>Les avis validés apparaîtront ici.</p></div>}
          <div className="review-list">{reviews.map((review) => <article className="review-card" key={review.id}><div className="review-card-top"><div className="review-author"><Avatar size="default" className="review-author-avatar"><AvatarImage src={review.profilePhotoUrl || undefined} alt="" /><AvatarFallback>{review.author_name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar><div className="review-author-copy"><strong>{review.author_name}</strong><div className="review-author-details"><Badge variant="secondary">{review.service_name}</Badge><time dateTime={review.created_at}>{formatReviewDate(review.created_at)}</time></div></div></div><Bubble className="review-rating-bubble" variant="tinted" role="img" aria-label={`Note : ${review.rating} étoiles sur 5`}><BubbleContent><Stars rating={review.rating} /><span>{review.rating}/5</span></BubbleContent></Bubble></div><Separator /><ReviewConversation initial={{ author_name: review.author_name, content: review.content, title: review.title, author_role: "client", created_at: review.created_at, profile_photo_url: review.profilePhotoUrl, attachment_url: review.photoUrl, attachment_name: review.photoUrl ? "Photo du résultat" : null }} comments={review.comments || []} />{review.canReply && (replyingId === review.id ? <form className="review-reply-form" onSubmit={(event) => void replyToReview(event, review.id)}><label htmlFor={`reply-${review.id}`}>Votre réponse</label><Textarea id={`reply-${review.id}`} value={replyText} onChange={(event) => setReplyText(event.target.value)} maxLength={1200} minLength={2} rows={3} placeholder="Répondre à la propriétaire…" required /><div><Button variant="default" type="submit" disabled={replying}>{replying ? "Envoi…" : "Publier la réponse"}</Button><Button variant="ghost" type="button" onClick={() => { setReplyingId(null); setReplyText(""); }}>Annuler</Button></div></form> : <Button className="review-reply-trigger" variant="link" type="button" onClick={() => setReplyingId(review.id)}>Répondre à la propriétaire</Button>)}</article>)}</div>
        </div>

        <aside className="review-form-card">
          <p className="section-label">Partager une expérience</p>
          <h2>Laisser un avis</h2>
          <p>Un avis peut être déposé après un rendez-vous marqué comme terminé, depuis le compte connecté ou le navigateur ayant effectué la demande. Il sera relu avant publication.</p>
          {success && <div className="success-banner"><CheckCircle2 size={19} /> Avis envoyé et en attente de validation.</div>}
          {eligibleAppointments.length ? (
            <form className="booking-form" onSubmit={submit}>
              <label>Rendez-vous<select name="appointmentId" required>{eligibleAppointments.map((appointment) => <option value={appointment.id} key={appointment.id}>{appointment.service_name} — {new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "long", year: "numeric" }).format(new Date(appointment.start_at.replace(" ", "T")))}</option>)}</select></label>
              <div className="form-row"><label>Prénom affiché *<input name="authorName" required maxLength={100} /></label><label>Note *<select name="rating" required defaultValue="5"><option value="5">5 — Excellent</option><option value="4">4 — Très bien</option><option value="3">3 — Bien</option><option value="2">2 — Moyen</option><option value="1">1 — Décevant</option></select></label></div>
              <label>Titre <span>(optionnel)</span><input name="title" maxLength={120} /></label>
              <label>Votre avis *<textarea name="content" required minLength={10} maxLength={1200} rows={6} /></label>
              <label className="photo-upload-label"><span><FileImage size={18} /> Photo de votre résultat <small>(optionnelle)</small></span><input name="reviewPhoto" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setPhotoName(event.target.files?.[0]?.name || "")} /><i>{photoName || "JPG, PNG ou WebP — 8 Mo maximum"}</i></label>
              {photoName && <Attachment size="sm"><AttachmentMedia><FileImage /></AttachmentMedia><AttachmentContent><AttachmentTitle>{photoName}</AttachmentTitle><AttachmentDescription>Photo jointe à l’avis</AttachmentDescription></AttachmentContent></Attachment>}
              <label className="honeypot" aria-hidden="true">Site web<input name="website" tabIndex={-1} autoComplete="off" /></label>
              <label className="checkbox-label"><input required name="acceptedPrivacy" type="checkbox" /> <span>J’accepte le traitement de cet avis selon la <Link href="/confidentialite" target="_blank">politique de confidentialité</Link>.</span></label>
              {error && <p className="form-error">{error}</p>}
              <button className="button button-primary button-full" disabled={submitting}>{submitting ? <><LoaderCircle className="spin" size={18} /> Envoi…</> : "Envoyer mon avis"}</button>
            </form>
          ) : <div className="review-locked"><p>Aucun rendez-vous terminé et non évalué n’est associé à ce compte ou à ce navigateur.</p><div className="review-locked-actions"><Link className="button button-ghost" href="/espace-client">Espace client</Link><Link className="button button-ghost" href="/mes-demandes">Sans compte</Link></div></div>}
        </aside>
      </div>
    </section>
  );
}
